import {useCallback} from 'react';
import {produce} from 'immer';
import type {
  FilterModel,
  PaginationModel,
  Sorting,
} from 'services/settings/types';
import type {ColumnConfig} from '../types';
import {useSchemaDataAccess} from '../../contexts';

const DEFAULT_PAGINATION_MODEL: PaginationModel = {
  page: 0,
  pageSize: 20,
};

export type ColumnConfigVisibility = {
  name: string;
  visible: boolean;
};

export type ColumnConfigStateProps = {
  columnConfigs: ColumnConfig[] | undefined;
  setColumnConfigs: (columnConfigs: ColumnConfig[]) => void;
  setColumnConfigVisibility: (columnName: string, visible: boolean) => void;
  updateColumnConfigsVisibility: (updates: ColumnConfigVisibility[]) => void;
  setColumnConfigsVisibilities: (
    columnNames: string[],
    visible?: boolean,
  ) => void;
  setColumnConfigWidth: (columnName: string, width: number) => void;
  sortingConfig?: Sorting;
  setSortingConfig: (sorting?: Sorting) => void;
  filterModel?: FilterModel | undefined;
  setFilterModel: (filterModel?: FilterModel) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (paginationModel: PaginationModel) => void;
  resetPaginationModel: () => void;
};

export const useColumnConfigState = (
  schemaName: string,
): ColumnConfigStateProps => {
  const {
    columnConfigs,
    sortingConfig,
    filterModel,
    paginationModel: persistedPaginationModel,
    setColumnConfigs,
    setSortingConfig,
    setFilterModel,
    setPaginationModel,
    resetPaginationModel: resetPaginationModelToPageSize,
  } = useSchemaDataAccess().useTableConfig(schemaName);

  const paginationModel = persistedPaginationModel ?? DEFAULT_PAGINATION_MODEL;

  const resetPaginationModel = useCallback(() => {
    resetPaginationModelToPageSize(paginationModel.pageSize);
  }, [resetPaginationModelToPageSize, paginationModel.pageSize]);

  const setColumnConfigVisibility = useCallback(
    (columnName: string, visible: boolean) => {
      const newColumnConfigState = produce(columnConfigs ?? [], draft => {
        const columnConfig = draft.find(s => s.name === columnName);
        if (columnConfig) {
          columnConfig.visible = visible;
        } else {
          draft.push({
            name: columnName,
            visible: visible,
          });
        }
      });

      setColumnConfigs(newColumnConfigState);
    },
    [columnConfigs, setColumnConfigs],
  );

  /**
   * Applies several visibility changes as one update
   */
  const updateColumnConfigsVisibility = useCallback(
    (updates: ColumnConfigVisibility[]) => {
      const newColumnConfigState = produce(columnConfigs ?? [], draft => {
        updates.forEach(({name, visible}) => {
          const columnConfig = draft.find(s => s.name === name);
          if (columnConfig) {
            columnConfig.visible = visible;
          } else {
            draft.push({name, visible});
          }
        });
      });

      setColumnConfigs(newColumnConfigState);
    },
    [columnConfigs, setColumnConfigs],
  );

  const setColumnConfigsVisibilities = useCallback(
    (columnNames: string[], visible: boolean = true) => {
      const newColumnConfigs = produce(columnConfigs ?? [], draft => {
        // Set all visibles to oposite value (e.q. all to invisible)
        draft.forEach(columnConfig => {
          columnConfig.visible = !visible;
        });

        // Update all columnNames to provided visiblity (e.g. visible)
        columnNames.forEach(columnName => {
          const columnConfig = draft.find(c => c.name === columnName);
          if (columnConfig) {
            columnConfig.visible = visible;
          } else {
            draft.push({
              name: columnName,
              visible: visible,
            });
          }
        });
      });

      setColumnConfigs(newColumnConfigs);
    },
    [columnConfigs, setColumnConfigs],
  );

  const setColumnConfigWidth = useCallback(
    (columnName: string, width: number) => {
      const newColumnConfigState = produce(columnConfigs ?? [], draft => {
        const columnConfig = draft.find(s => s.name === columnName);
        if (columnConfig) {
          columnConfig.width = width;
        } else {
          draft.push({
            name: columnName,
            width: width,
            visible: true, // Default true when handled
          });
        }
      });

      setColumnConfigs(newColumnConfigState);
    },
    [columnConfigs, setColumnConfigs],
  );

  return {
    columnConfigs,
    setColumnConfigs,
    setColumnConfigVisibility,
    updateColumnConfigsVisibility,
    setColumnConfigsVisibilities,
    setColumnConfigWidth,
    sortingConfig,
    setSortingConfig,
    filterModel,
    setFilterModel,
    paginationModel,
    setPaginationModel,
    resetPaginationModel,
  };
};
